import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Youtube, 
  Plus, 
  Trash2, 
  Loader2, 
  ExternalLink,
  Play,
  Pause,
  RefreshCw,
  Download,
  Eye,
  EyeOff,
  Pencil,
  Check,
  X,
  BarChart3,
  Users,
  MousePointerClick,
  TrendingUp,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const MotivationalVideosManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [previewVideoId, setPreviewVideoId] = useState(null);
  
  // Analytics state
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);
  
  // Fetch Motivate Me analytics
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/motivate-me/analytics`, { withCredentials: true });
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };
  
  // Open analytics modal
  const handleOpenAnalytics = () => {
    setShowAnalytics(true);
    fetchAnalytics();
  };

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/motivational-videos`, { withCredentials: true });
      setVideos(res.data.videos || []);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      toast.error('Failed to load motivational videos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!newVideoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    setAdding(true);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/motivational-videos`, {
        youtube_url: newVideoUrl.trim(),
        title: newVideoTitle.trim() || null
      }, { withCredentials: true });

      setVideos(prev => [res.data.video, ...prev]);
      setNewVideoUrl('');
      setNewVideoTitle('');
      toast.success('Video added successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add video');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${API_BASE}/api/admin/motivational-videos/${videoId}`, { withCredentials: true });
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const handleToggleActive = async (video) => {
    try {
      const res = await axios.put(`${API_BASE}/api/admin/motivational-videos/${video.id}`, {
        is_active: !video.is_active
      }, { withCredentials: true });
      
      setVideos(prev => prev.map(v => v.id === video.id ? res.data.video : v));
      toast.success(video.is_active ? 'Video disabled' : 'Video enabled');
    } catch (err) {
      toast.error('Failed to update video');
    }
  };

  const handleSaveTitle = async (video) => {
    if (!editTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      const res = await axios.put(`${API_BASE}/api/admin/motivational-videos/${video.id}`, {
        title: editTitle.trim()
      }, { withCredentials: true });
      
      setVideos(prev => prev.map(v => v.id === video.id ? res.data.video : v));
      setEditingId(null);
      toast.success('Title updated');
    } catch (err) {
      toast.error('Failed to update title');
    }
  };

  const handleBulkImport = async () => {
    if (!window.confirm('This will import the 18 default motivational videos. Continue?')) return;

    setImporting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/motivational-videos/bulk-import`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchVideos();
    } catch (err) {
      console.error('Import error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to import videos';
      toast.error(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  const getYoutubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Youtube className="w-6 h-6 text-red-500" />
            Video Embeds
          </h2>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Manage embedded videos shown to users based on their membership tier
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenAnalytics}
            className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
            data-testid="motivate-me-analytics-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkImport}
            disabled={importing}
            className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Import Defaults
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchVideos}
            className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Add Video Form */}
      <form onSubmit={handleAddVideo} className="bg-[#2D063E]/50 border border-[#4A0A66] rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <Input
              placeholder="YouTube URL (e.g., https://youtube.com/watch?v=...)"
              value={newVideoUrl}
              onChange={(e) => setNewVideoUrl(e.target.value)}
              className="bg-[#1a0527] border-[#4A0A66] text-white"
            />
          </div>
          <div className="md:w-48">
            <Input
              placeholder="Title (optional)"
              value={newVideoTitle}
              onChange={(e) => setNewVideoTitle(e.target.value)}
              className="bg-[#1a0527] border-[#4A0A66] text-white"
            />
          </div>
          <Button
            type="submit"
            disabled={adding || !newVideoUrl.trim()}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
          >
            {adding ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Add Video
          </Button>
        </div>
      </form>

      {/* Stats */}
      <div className="flex gap-4 text-sm">
        <span className="text-[#FDF2F8]/60">
          Total: <span className="text-white font-medium">{videos.length}</span>
        </span>
        <span className="text-[#FDF2F8]/60">
          Active: <span className="text-green-400 font-medium">{videos.filter(v => v.is_active).length}</span>
        </span>
        <span className="text-[#FDF2F8]/60">
          Disabled: <span className="text-yellow-400 font-medium">{videos.filter(v => !v.is_active).length}</span>
        </span>
      </div>

      {/* Video List */}
      {videos.length === 0 ? (
        <div className="bg-[#2D063E]/30 border border-[#4A0A66] rounded-xl p-8 text-center">
          <Youtube className="w-12 h-12 text-[#FDF2F8]/30 mx-auto mb-3" />
          <p className="text-[#FDF2F8]/60">No motivational videos yet</p>
          <p className="text-sm text-[#FDF2F8]/40 mt-1">
            Add YouTube videos or click Import Defaults to get started
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {videos.map((video) => (
            <div
              key={video.id}
              className={`bg-[#2D063E]/50 border rounded-xl p-4 transition-all ${
                video.is_active ? 'border-[#4A0A66]' : 'border-yellow-500/30 opacity-60'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Thumbnail */}
                <div 
                  className="relative w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer group"
                  onClick={() => setPreviewVideoId(previewVideoId === video.video_id ? null : video.video_id)}
                >
                  <img
                    src={getYoutubeThumbnail(video.video_id)}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {previewVideoId === video.video_id ? (
                      <Pause className="w-8 h-8 text-white" />
                    ) : (
                      <Play className="w-8 h-8 text-white" />
                    )}
                  </div>
                  {!video.is_active && (
                    <div className="absolute top-1 right-1 bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded font-medium">
                      Disabled
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  {editingId === video.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="bg-[#1a0527] border-[#4A0A66] text-white h-8 text-sm"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSaveTitle(video)}
                        className="text-green-400 hover:text-green-300 h-8 w-8 p-0"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                        className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium truncate">{video.title}</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(video.id);
                          setEditTitle(video.title);
                        }}
                        className="text-[#FDF2F8]/40 hover:text-[#FDF2F8]/70 h-6 w-6 p-0"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-[#FDF2F8]/40 mt-1 truncate">
                    ID: {video.video_id}
                  </p>
                  <a
                    href={video.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0FFFE2] hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Open in YouTube <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggleActive(video)}
                    className={video.is_active ? 'text-green-400 hover:text-green-300' : 'text-yellow-400 hover:text-yellow-300'}
                  >
                    {video.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteVideo(video.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Preview Player */}
              {previewVideoId === video.video_id && (
                <div className="mt-4 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Analytics Modal */}
      {showAnalytics && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="motivate-me-analytics-modal">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FF7901]" />
                Motivate Me Analytics
              </h3>
              <button
                onClick={() => setShowAnalytics(false)}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-[#FDF2F8]/70 hover:text-white"
                data-testid="close-analytics-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
                </div>
              ) : analyticsData ? (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <MousePointerClick className="w-6 h-6 mx-auto mb-2 text-[#df1283]" />
                      <div className="text-2xl font-bold text-white">{analyticsData.summary?.total_button_clicks || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Total Clicks</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <Play className="w-6 h-6 mx-auto mb-2 text-[#0FFFE2]" />
                      <div className="text-2xl font-bold text-white">{analyticsData.summary?.total_video_plays || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Video Plays</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <Users className="w-6 h-6 mx-auto mb-2 text-[#FF7901]" />
                      <div className="text-2xl font-bold text-white">{analyticsData.summary?.unique_users || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Unique Users</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-400" />
                      <div className="text-2xl font-bold text-white">{analyticsData.summary?.avg_clicks_per_user || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Avg Clicks/User</div>
                    </div>
                  </div>

                  {/* Top Users Section */}
                  <div>
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#df1283]" />
                      Top Users by Button Clicks
                    </h4>
                    {analyticsData.top_users && analyticsData.top_users.length > 0 ? (
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#4A0A66]/30 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-[#FDF2F8]/70">User</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Email</th>
                              <th className="text-center p-2 text-[#FDF2F8]/70">Clicks</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Last Click</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsData.top_users.map((user, idx) => (
                              <tr key={user.user_id || idx} className="border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20">
                                <td className="p-2 text-white">{user.name || 'Unknown'}</td>
                                <td className="p-2 text-[#FDF2F8]/70">{user.email || '-'}</td>
                                <td className="p-2 text-center">
                                  <span className="px-2 py-0.5 bg-[#df1283]/20 text-[#df1283] rounded-full text-xs">
                                    {user.click_count}
                                  </span>
                                </td>
                                <td className="p-2 text-[#FDF2F8]/50 text-xs">
                                  {user.last_click ? new Date(user.last_click).toLocaleString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[#FDF2F8]/50 py-4 text-center bg-[#4A0A66]/20 rounded-lg">
                        No user click data yet
                      </p>
                    )}
                  </div>

                  {/* Video Stats Section */}
                  <div>
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Youtube className="w-4 h-4 text-red-500" />
                      Video Play Statistics
                    </h4>
                    {analyticsData.video_stats && analyticsData.video_stats.length > 0 ? (
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#4A0A66]/30 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Video</th>
                              <th className="text-center p-2 text-[#FDF2F8]/70">Plays</th>
                              <th className="text-center p-2 text-[#FDF2F8]/70">Unique Viewers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsData.video_stats.map((video, idx) => (
                              <tr key={video.video_id || idx} className="border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20">
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={`https://img.youtube.com/vi/${video.video_id}/default.jpg`}
                                      alt={video.title}
                                      className="w-10 h-7 object-cover rounded"
                                    />
                                    <span className="text-white truncate max-w-[200px]">{video.title || 'Unknown'}</span>
                                  </div>
                                </td>
                                <td className="p-2 text-center">
                                  <span className="px-2 py-0.5 bg-[#0FFFE2]/20 text-[#0FFFE2] rounded-full text-xs">
                                    {video.play_count}
                                  </span>
                                </td>
                                <td className="p-2 text-center text-white">{video.unique_viewers}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[#FDF2F8]/50 py-4 text-center bg-[#4A0A66]/20 rounded-lg">
                        No video play data yet
                      </p>
                    )}
                  </div>

                  {/* Clicks by Day Chart */}
                  {analyticsData.clicks_by_day && analyticsData.clicks_by_day.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[#0FFFE2]" />
                        Daily Clicks (Last 30 Days)
                      </h4>
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 p-4">
                        <div className="flex items-end gap-1 h-32">
                          {analyticsData.clicks_by_day.slice(0, 30).reverse().map((day, idx) => {
                            const maxClicks = Math.max(...analyticsData.clicks_by_day.map(d => d.clicks));
                            const height = maxClicks > 0 ? (day.clicks / maxClicks) * 100 : 0;
                            return (
                              <div
                                key={day.date || idx}
                                className="flex-1 bg-gradient-to-t from-[#df1283] to-[#FF7901] rounded-t hover:opacity-80 transition-opacity cursor-pointer group relative"
                                style={{ height: `${Math.max(height, 4)}%` }}
                                title={`${day.date}: ${day.clicks} clicks`}
                              >
                                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#2D063E] border border-[#4A0A66] px-2 py-1 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  {day.date}: {day.clicks}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-[#FDF2F8]/40">
                          <span>{analyticsData.clicks_by_day[analyticsData.clicks_by_day.length - 1]?.date || ''}</span>
                          <span>{analyticsData.clicks_by_day[0]?.date || ''}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-[#FDF2F8]/50">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No analytics data yet.</p>
                  <p className="text-xs mt-1">Data will appear once users click the Motivate Me! button.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotivationalVideosManager;
